/**
 * Board WebSocket Hook
 * ====================
 * Real-time updates for all boards (Call Center, Sales, Operations).
 * Automatically reconnects and handles subscriptions.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const WS_RECONNECT_DELAY = 3000; // 3 seconds
const WS_PING_INTERVAL = 30000; // 30 seconds
const MAX_RECONNECT_ATTEMPTS = 10;

/**
 * Hook for real-time board updates via WebSocket
 */
export function useBoardWebSocket(boardType = 'all', options = {}) {
  const {
    onLeadUpdate,
    onProjectUpdate,
    onAssignmentChange,
    onBulkUpdate,
    onConnect,
    onDisconnect,
    enabled = true,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isManualClose = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const boardTypeRef = useRef(boardType);
  const connectFnRef = useRef(null);
  
  // Keep boardType ref updated
  useEffect(() => {
    boardTypeRef.current = boardType;
  }, [boardType]);
  
  // Store callbacks in refs to avoid stale closures
  const callbacksRef = useRef({ onLeadUpdate, onProjectUpdate, onAssignmentChange, onBulkUpdate, onConnect, onDisconnect });
  useEffect(() => {
    callbacksRef.current = { onLeadUpdate, onProjectUpdate, onAssignmentChange, onBulkUpdate, onConnect, onDisconnect };
  }, [onLeadUpdate, onProjectUpdate, onAssignmentChange, onBulkUpdate, onConnect, onDisconnect]);

  // Get WebSocket URL
  const getWsUrl = useCallback(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (!token) return null;

    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Convert http(s) to ws(s)
    const wsBase = baseUrl.replace(/^http/, 'ws');
    return `${wsBase}/api/ws/boards?token=${token}`;
  }, []);

  // Handle incoming messages
  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      setLastMessage(data);

      const callbacks = callbacksRef.current;

      switch (data.type) {
        case 'connected':
          console.log('[BoardWS] Connected:', data.message);
          break;

        case 'subscribed':
          console.log(`[BoardWS] Subscribed to ${data.board}`);
          break;

        case 'lead_update':
          console.log(`[BoardWS] Lead update (${data.update_type}):`, data.data?.id);
          callbacks.onLeadUpdate?.(data.data, data.update_type, data.board);
          break;

        case 'project_update':
          console.log(`[BoardWS] Project update (${data.update_type}):`, data.data?.id);
          callbacks.onProjectUpdate?.(data.data, data.update_type, data.board);
          break;

        case 'assignment_change':
          console.log(`[BoardWS] Assignment change:`, data.record_id);
          callbacks.onAssignmentChange?.(data);
          break;

        case 'bulk_update':
          console.log(`[BoardWS] Bulk update for ${data.board}:`, data.message);
          callbacks.onBulkUpdate?.(data.board, data.message);
          break;

        case 'pong':
          // Heartbeat response
          break;

        default:
          console.log('[BoardWS] Unknown message type:', data.type);
      }
    } catch (err) {
      console.error('[BoardWS] Error parsing message:', err);
    }
  }, []);

  // Define connect function and store in ref for reconnection
  useEffect(() => {
    const doConnect = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        return;
      }

      const url = getWsUrl();
      if (!url) {
        console.warn('[BoardWS] No auth token available');
        return;
      }

      try {
        console.log('[BoardWS] Connecting...');
        isManualClose.current = false;
        
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('[BoardWS] Connected');
          setIsConnected(true);
          setConnectionError(null);
          setReconnectAttempts(0);
          reconnectAttemptsRef.current = 0;

          // Subscribe to the board
          ws.send(JSON.stringify({ type: 'subscribe', board: boardTypeRef.current }));

          // Start ping interval
          pingIntervalRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'ping' }));
            }
          }, WS_PING_INTERVAL);

          callbacksRef.current.onConnect?.();
        };

        ws.onmessage = handleMessage;

        ws.onclose = (event) => {
          console.log('[BoardWS] Disconnected:', event.code, event.reason);
          setIsConnected(false);
          
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }

          callbacksRef.current.onDisconnect?.();

          // Attempt reconnection if not manually closed
          if (!isManualClose.current && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
            reconnectAttemptsRef.current += 1;
            setReconnectAttempts(reconnectAttemptsRef.current);
            console.log(`[BoardWS] Reconnecting in ${WS_RECONNECT_DELAY}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`);
            reconnectTimeoutRef.current = setTimeout(() => {
              connectFnRef.current?.();
            }, WS_RECONNECT_DELAY);
          }
        };

        ws.onerror = (error) => {
          console.error('[BoardWS] Error:', error);
          setConnectionError('WebSocket connection error');
        };

      } catch (err) {
        console.error('[BoardWS] Failed to create WebSocket:', err);
        setConnectionError(err.message);
      }
    };

    connectFnRef.current = doConnect;
  }, [getWsUrl, handleMessage]);

  // Connect function wrapper
  const connect = useCallback(() => {
    connectFnRef.current?.();
  }, []);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    isManualClose.current = true;
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
  }, []);

  // Subscribe to a different board
  const subscribeTo = useCallback((newBoardType) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe', board: newBoardType }));
    }
  }, []);

  // Unsubscribe from a board
  const unsubscribeFrom = useCallback((boardToLeave) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'unsubscribe', board: boardToLeave }));
    }
  }, []);

  // Effect to manage connection lifecycle
  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  // Reconnect when board type changes
  useEffect(() => {
    if (isConnected && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe', board: boardType }));
    }
  }, [boardType, isConnected]);

  return {
    isConnected,
    lastMessage,
    connectionError,
    reconnectAttempts,
    connect,
    disconnect,
    subscribeTo,
    unsubscribeFrom,
  };
}

export default useBoardWebSocket;
